import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Save, X, ArrowUp, ArrowDown, AlertCircle, Eye, EyeOff, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const CATEGORY_LABELS = {
    "crisis-hotline": "Crisis hotlines",
    "medical": "Medical",
    "mental-health": "Mental health",
    "safety-classes": "Safety classes",
    "education": "Education",
    "legal": "Legal aid",
    "family-services": "Family services",
    "food-assistance": "Food assistance",
    "veterans": "Veterans",
    "addiction-recovery": "Addiction recovery",
    "other": "Other",
};

const blank = {
    name: "", category: "other", description: "",
    phone: "", email: "", website: "", address: "", hours: "",
    is_emergency: false, active: true, tags: [],
};

const DirectoryPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState(blank);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/directory");
            setRows(data || []);
        } catch { toast.error("Could not load directory"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const reset = () => { setAdding(false); setEditingId(null); setForm(blank); };

    const startEdit = (r) => {
        setEditingId(r.id); setAdding(false);
        setForm({
            name: r.name, category: r.category, description: r.description || "",
            phone: r.phone || "", email: r.email || "", website: r.website || "",
            address: r.address || "", hours: r.hours || "",
            is_emergency: r.is_emergency, active: r.active, tags: r.tags || [],
        });
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        try {
            if (editingId) await api.patch(`/admin/directory/${editingId}`, form);
            else await api.post("/admin/directory", form);
            toast.success("Saved");
            reset();
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete "${r.name}"?`)) return;
        try { await api.delete(`/admin/directory/${r.id}`); toast.success("Deleted"); await load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggleActive = async (r) => {
        try { await api.patch(`/admin/directory/${r.id}`, { active: !r.active }); await load(); }
        catch { toast.error("Update failed"); }
    };

    const reorder = async (idx, delta) => {
        const j = idx + delta;
        if (j < 0 || j >= rows.length) return;
        const a = rows[idx], b = rows[j];
        try {
            await api.post("/admin/directory/reorder", [{ id: a.id, order: b.order }, { id: b.id, order: a.order }]);
            await load();
        } catch { toast.error("Reorder failed"); }
    };

    return (
        <div className="space-y-6" data-testid="directory-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Community directory</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Curated contacts for the community — crisis lines, doctors, safety instructors, legal aid. Mark a row as <strong>Crisis</strong> to pin it to the top of the public <code>/directory</code> page.</p>
                </div>
                <button onClick={() => { setAdding(true); setEditingId(null); setForm(blank); }} data-testid="directory-add-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add entry
                </button>
            </div>

            {(adding || editingId) && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="directory-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Name *</label>
                            <input data-testid="directory-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="988 Suicide & Crisis Lifeline" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Category</label>
                            <select data-testid="directory-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40">
                                {Object.entries(CATEGORY_LABELS).map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Description</label>
                        <textarea data-testid="directory-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input data-testid="directory-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                        <input data-testid="directory-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                        <input data-testid="directory-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input data-testid="directory-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                        <input data-testid="directory-hours-input" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Hours (e.g. 24/7 or Mon-Fri 9-5)" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                        <label className="flex items-center gap-2 text-sm text-[#1C252E]">
                            <input type="checkbox" data-testid="directory-emergency-checkbox" checked={form.is_emergency} onChange={(e) => setForm({ ...form, is_emergency: e.target.checked })} /> Crisis / emergency (pin to top, red border)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-[#1C252E]">
                            <input type="checkbox" data-testid="directory-active-checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visible on public page
                        </label>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={reset} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                        <button onClick={save} data-testid="directory-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium"><Save className="w-4 h-4" strokeWidth={2} /> {editingId ? "Save" : "Add"}</button>
                    </div>
                </div>
            )}

            {loading ? <p className="text-sm text-[#4A5568]">Loading…</p> : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="directory-empty">No entries yet.</p>
            ) : (
                <ul className="space-y-2" data-testid="directory-list">
                    {rows.map((r, idx) => (
                        <li key={r.id} data-testid={`directory-row-${r.id}`} className={`p-4 rounded-2xl border ${r.is_emergency ? "bg-white border-[#E53935]/40 ring-1 ring-[#E53935]/20" : "bg-white border-black/10"} ${!r.active && "opacity-60"}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {r.is_emergency && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#E53935] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Crisis</span>}
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{CATEGORY_LABELS[r.category] || r.category}</span>
                                    </div>
                                    <p className="font-medium text-[#1C252E] mt-1">{r.name}</p>
                                    {r.phone && <p className="text-xs text-[#4A5568] mt-0.5"><Phone className="inline w-3 h-3 -mt-0.5" /> {r.phone}</p>}
                                    {r.description && <p className="text-xs text-[#4A5568] mt-0.5 line-clamp-2">{r.description}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => reorder(idx, -1)} disabled={idx === 0} data-testid={`directory-up-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30" aria-label="Up"><ArrowUp className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => reorder(idx, +1)} disabled={idx === rows.length - 1} data-testid={`directory-down-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30" aria-label="Down"><ArrowDown className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => toggleActive(r)} data-testid={`directory-toggle-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Toggle">{r.active ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}</button>
                                    <button onClick={() => startEdit(r)} data-testid={`directory-edit-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Edit"><Edit2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => remove(r)} data-testid={`directory-delete-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10" aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DirectoryPanel;
